import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import "../styles/CombinedComponent.css";
import { NoDataUploaded } from "./NoDataUploaded.js";
import { NavBar } from "./Nav/NavBar.js";
import { DataContext } from "../providers/DataProvider.js";
import { LargeGraphOptions } from "./Graphing/config/LargeGraphOptions.js";
import { LargeGraph } from "./Graphing/LargeGraph/LargeGraph.js";
import { LargeGraphControls } from "./Graphing/LargeGraph/LargeGraphControls.js";
import { MiniGraphGrid } from "./Graphing/MiniGraphGrid/MiniGraphGrid.js";
import { MiniGraphControls } from "./Graphing/MiniGraphGrid/MiniGraphControls.js";
import { FilteredGraph } from "./Graphing/FilteredData/FilteredGraph.js";
import { FilteredGraphOptions } from "./Graphing/config/FilteredGraphOptions.js";
import { FilterControls } from "./Graphing/FilteredData/FilterControls.js";
import { MetricsControls } from "./Graphing/Metrics/MetricsControls.js";
import { Chart, registerables } from "chart.js";
import Heatmap from "./Graphing/Metrics/Heatmap.js";
import "chartjs-adapter-date-fns";
import {
  handleWellArrayClick,
  handleAllSelectorClick,
  handleRowSelectorClick,
  handleColumnSelectorClick,
  handleScreenshot,
} from "../utilities/Handlers.js";
import deepEqual from "fast-deep-equal"; // for deep comparison of project state
import annotationPlugin from "chartjs-plugin-annotation";
import zoomPlugin from "chartjs-plugin-zoom";
import useFilterWorkerPool from "../utilities/useFilterWorkerPool.js";
import {
  mergeFilteredBack,
  splitPhasesByControlSubtraction,
  serializeFilter,
  computeControlAveragesFromPacked,
  computeMedianFoByIndicatorFromPacked,
} from "../utilities/filterPack.js";
import html2canvas from "html2canvas";
import { IconButton, Tooltip, Typography, Button } from "@mui/material";
import { AddAPhotoTwoTone } from "@mui/icons-material";
import { motion } from "framer-motion";
import { PERMISSIONS } from "../permissions";

// Register Chart.js components and plugins
Chart.register(...registerables, annotationPlugin, zoomPlugin);

// Main component that integrates various functionalities
export const CombinedComponent = ({ profile, setProfile }) => {
  const {
    project,
    setProject,
    wellArrays,
    rowLabels,
    extractedIndicatorTimes,
    showFiltered,
    setShowFiltered,
    selectedWellArray,
    setSelectedWellArray,
    enabledFilters,
    extractedIndicators,
    savedMetrics,
    selectedFilters,
    annotations,
    setAnnotations,
    overlayRawAndFiltered,
  } = useContext(DataContext);

  // Ref to store the previous project state for comparison
  const prevProjectRef = useRef(null);

  // Zoom and Pan state for LargeGraph
  const [zoomState, setZoomState] = useState(true);
  const [zoomMode, setZoomMode] = useState("xy");
  const [panState, setPanState] = useState(true);
  const [panMode, setPanMode] = useState("xy");
  // Ref to access LargeGraph's chart instance
  const largeGraphRef = useRef(null);

  // Ref to access each component instance for use in screenshotting
  const combinedComponentRef = useRef(null);
  const miniGraphGridComponentRef = useRef(null);
  const largeGraphComponentRef = useRef(null);
  const heatmapComponentRef = useRef(null);
  const filteredGraphComponentRef = useRef(null);

  // Canvas dimensions for graphs

  const [largeCanvasWidth, setLargeCanvasWidth] = useState(
    window.innerWidth / 2.3
    // window.innerWidth / 2.5
  );
  const [largeCanvasHeight, setLargeCanvasHeight] = useState(
    window.innerHeight / 2.3
    // window.innerHeight / 2.5
  );
  const [smallCanvasWidth, setSmallCanvasWidth] = useState(
    window.innerWidth / 64.4
    // window.innerWidth / 70
  );
  const [smallCanvasHeight, setSmallCanvasHeight] = useState(
    window.innerHeight / 46
    // window.innerHeight / 50
  );

  const handleResize = () => {
    setLargeCanvasWidth(window.innerWidth / 2.3);
    setLargeCanvasHeight(window.innerHeight / 2.3);
    setSmallCanvasWidth(window.innerWidth / 64.4);
    setSmallCanvasHeight(window.innerHeight / 46);
  };

  // State for MiniGraph management
  const [isFiltered, setIsFiltered] = useState(false); // Default is raw data (false)

  // State to control LargeGraph visibility
  const [showLargeGraph, setShowLargeGraph] = useState(true);

  // State variables to store range of y-values inside filteredGraph's annotation box
  // const [annotations, setAnnotations] = useState([]);
  const [annotationRangeStart, setAnnotationRangeStart] = useState(null);
  const [annotationRangeEnd, setAnnotationRangeEnd] = useState(null);

  // State variable handling what metrics type is shown in heatmap
  const [metricType, setMetricType] = useState("Max"); // Default metric type
  const [metricIndicator, setMetricIndicator] = useState(0); // Defaults to first indicator

  // State for tracking filter application progress
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [isLoadingFilterResults, setIsLoadingFilterResults] = useState(false);
  const [filterProgress, setFilterProgress] = useState(null);

  // Y-axis scale source per graph: "all" → whole-plate range (default,
  // good for comparing wells in context); "selected" → auto-zoom to the
  // selected wells' range (good for reading detail).
  const [largeGraphYScaleMode, setLargeGraphYScaleMode] = useState("all");
  const [filteredGraphYScaleMode, setFilteredGraphYScaleMode] = useState("all");

  const filterPool = useFilterWorkerPool();

  // Extracted plate and experiment data from the project
  const plate = project?.plate || [];

  // Generating labels for columns and rows
  const columnLabels = Array.from(
    { length: plate[0]?.numberOfColumns || 0 },
    (_, i) => i + 1
  );

  // Effect to listen to window resize events
  useEffect(() => {
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Shows window alert before refresh
  useEffect(() => {
    const onBeforeUnload = (ev) => {
      ev.returnValue = "prevent reload";
      return "prevent reload";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  // Functions to handle zoom state changes
  const toggleZoomState = (currentZoomState) => {
    setZoomState(!currentZoomState);
  };
  const changeZoomMode = (mode) => {
    setZoomMode(mode);
  };
  // Functions to handle pan state changes
  const togglePanState = (currentPanState) => {
    setPanState(!currentPanState);
  };
  const changePanMode = (mode) => {
    setPanMode(mode);
  };

  // Reset zoom handler
  const resetZoom = () => {
    if (largeGraphRef.current) {
      largeGraphRef.current.resetZoom(); // Call resetZoom on the chart instance
    }
  };

  const applyEnabledFilters = async () => {
    setIsApplyingFilters(true);
    setIsLoadingFilterResults(true);
    setFilterProgress({ phaseIndex: 0, totalPhases: 0 });
    try {
      // Phase D: defer the pack to dispatch time. Phase 1 uses runFromRaw,
      // which packs each shard inline as it sends it to a worker — main
      // thread peak drops from ~770MB (pack-everything-upfront) to
      // ~770MB / numShards. Phases 2+ work off the already-packed result
      // and use the unchanged `run` API, since their input lives in
      // worker-transferred buffers, not rawXs/rawYs.
      const phases = splitPhasesByControlSubtraction(enabledFilters);
      let packed = null;
      for (let p = 0; p < phases.length; p++) {
        const phase = phases[p];
        const onProgress = (evt) => {
          setFilterProgress({
            phaseIndex: p,
            totalPhases: phases.length,
            filterIndex: evt && evt.filterIndex,
            totalFilters: evt && evt.totalFilters,
            shardsDone: evt && evt.shardsDone,
            totalShards: evt && evt.totalShards,
          });
        };
        if (phase.kind === "segment") {
          if (packed === null) {
            packed = await filterPool.runFromRaw({
              wellArrays,
              filters: phase.filters.map(serializeFilter),
              onProgress,
              shardable: true,
            });
          } else {
            packed = await filterPool.run({
              wells: packed,
              filters: phase.filters.map(serializeFilter),
              onProgress,
              shardable: true,
            });
          }
        } else if (phase.kind === "staticRatioRescale") {
          // Rescale-enabled StaticRatio runs un-sharded: the per-indicator
          // median Fo it multiplies by spans the whole plate, so the worker
          // pass needs the full packed array (and the scalar array indexed
          // by global indicator). Seed the packed state if this is the very
          // first phase (mirrors the ControlSubtraction path above).
          if (packed === null) {
            packed = await filterPool.runFromRaw({
              wellArrays,
              filters: [],
              shardable: false,
            });
          }
          const medianFoByIndicator = computeMedianFoByIndicatorFromPacked(
            packed,
            phase.filter
          );
          const spec = serializeFilter(phase.filter);
          packed = await filterPool.run({
            wells: packed,
            filters: [
              { ...spec, params: { ...spec.params, medianFoByIndicator } },
            ],
            onProgress,
            shardable: false,
          });
        } else {
          // ControlSubtraction must run un-sharded: its apply-well lookup is
          // by global row/col, which only matches the full packed array.
          if (packed === null) {
            // CS as the very first phase. runFromRaw with shardable:false
            // packs the whole plate once (equivalent to pre-Phase-D).
            const seedPacked = await filterPool.runFromRaw({
              wellArrays,
              filters: [],
              shardable: false,
            });
            packed = seedPacked;
          }
          const avgCurves = computeControlAveragesFromPacked(packed, phase.filter);
          packed = await filterPool.run({
            wells: packed,
            filters: [serializeFilter(phase.filter)],
            avgCurves,
            onProgress,
            shardable: false,
          });
        }
      }
      mergeFilteredBack(wellArrays, packed);

      // New wellArrays reference (same Indicator instances) so downstream
      // memoization detects the update.
      const updatedWellArrays = wellArrays.map((well) => ({
        ...well,
        indicators: [...well.indicators],
      }));
      const updatedProject = {
        ...project,
        plate: project.plate.map((plate) => ({
          ...plate,
          experiments: plate.experiments.map((experiment) => ({
            ...experiment,
            wells: updatedWellArrays,
          })),
        })),
      };
      setProject(updatedProject);
      const updatedSelectedWellArray = selectedWellArray.map(
        (selectedWell) =>
          updatedWellArrays.find((well) => well.id === selectedWell.id) ||
          selectedWell
      );
      setSelectedWellArray(updatedSelectedWellArray);
    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setIsApplyingFilters(false);
      setIsLoadingFilterResults(false);
      setFilterProgress(null);
    }
  };

  const handleResetFilteredData = () => {
    if (!wellArrays || !wellArrays.length) return;

    // Mutate existing Indicator instances in place to restore filtered =
    // raw, then create new outer well/array references so downstream
    // memoization detects the update. Same pattern as applyFilters →
    // mergeFilteredBack: identity-preserving so methods like
    // materializeFilteredData() survive the reset.
    //
    // Previously this spread `{...indicator}` produced plain objects
    // (dropping prototype methods) and read `rawData.map(...)` which is
    // empty post-Phase C — filteredData became `[]` and the filtered
    // chart went blank.
    for (const well of wellArrays) {
      for (const indicator of well.indicators) {
        if (typeof indicator.setFilteredTypedArrays === "function") {
          // Point filtered typed-arrays back at raw. Next applyFilters
          // call replaces these refs with new worker-produced arrays.
          indicator.setFilteredTypedArrays(indicator.rawXs, indicator.rawYs);
        } else {
          // Legacy path (shouldn't happen post-Phase C, but kept safe):
          // mirror raw {x,y}[] form into filteredData.
          indicator.filteredData = Array.isArray(indicator.rawData)
            ? indicator.rawData.map((p) => ({ ...p }))
            : [];
        }
      }
    }

    // New outer references — same Indicator INSTANCES — so consumers
    // memoized on wellArrays/selectedWellArray pick up the change.
    const updatedWellArrays = wellArrays.map((well) => ({
      ...well,
      indicators: [...well.indicators],
    }));

    if (typeof setProject === "function" && project) {
      const updatedProject = {
        ...project,
        plate: project.plate.map((plate) => ({
          ...plate,
          experiments: plate.experiments.map((experiment) => ({
            ...experiment,
            wells: updatedWellArrays,
          })),
        })),
      };
      setProject(updatedProject);
    }

    if (typeof setSelectedWellArray === "function" && selectedWellArray) {
      const updatedSelectedWellArray = selectedWellArray.map(
        (selectedWell) =>
          updatedWellArrays.find((well) => well.id === selectedWell.id) ||
          selectedWell
      );
      setSelectedWellArray(updatedSelectedWellArray);
    }
  };

  const handleToggleVisibility = (indicatorId) => {
    // Create a deep clone of the project to avoid mutating the original object directly
    const updatedProject = { ...project };
    updatedProject.plate = updatedProject.plate.map((plate) => ({
      ...plate,
      experiments: plate.experiments.map((experiment) => ({
        ...experiment,
        wells: experiment.wells.map((well) => {
          const updatedIndicators = well.indicators.map((indicator) => {
            if (indicator.id === indicatorId) {
              // Toggle the 'isDisplayed' state using the setDisplayed method
              const newIsDisplayed = !indicator.isDisplayed;
              indicator.setDisplayed(newIsDisplayed); // Directly toggle the property
            }
            return indicator;
          });
          return { ...well, indicators: updatedIndicators };
        }),
      })),
    }));
    let indicatorTimes = Object.values(extractedIndicatorTimes);
    // Update the project in the context
    // console.log("extractedIndicatorTimes: ", indicatorTimes[0]);
    // console.log("savedMetrics: ", savedMetrics);
    // console.log("selectedFilters: ", selectedFilters);
    console.log(updatedProject);
    setProject(updatedProject);
  };
  // Effect to track changes in project state
  useEffect(() => {
    if (!deepEqual(prevProjectRef.current, project)) {
      // console.log("Project Data:", project);

      // console.log("newInd: ", extractedIndicators);
      prevProjectRef.current = project; // Update the previous project reference only if there's a change
    }
  }, [project, wellArrays]);

  // Defined color palette to differentiate between different indicators
  const indicatorColors = [
    "rgb(75, 192, 192)", // Teal
    "rgb(255, 99, 132)", // Red
    "rgb(54, 162, 235)", // Blue
    "rgb(255, 206, 86)", // Yellow
    "rgb(153, 102, 255)", // Purple
    "rgb(255, 159, 64)", // Orange
  ];

  // Preparing graph data for raw and filtered graphs, using color differentiation for each indicator
  // console.log(extractedIndicatorTimes);
  let indicatorTimes = Object.values(extractedIndicatorTimes);

  // Helper to get a stable key for selectedWellArray (array of well IDs)
  const selectedWellIds = useMemo(
    () => selectedWellArray.map((well) => well.id).join(","),
    [selectedWellArray]
  );

  // Memoize rawGraphData to prevent unnecessary re-renders. Materialize
  // {x,y}[] only for the small set of selected wells — raw storage is
  // typed-array post-load (Phase C), so the full {x,y}[] view across all
  // wells would re-OOM the renderer.
  const rawGraphData = useMemo(
    () => ({
      labels: indicatorTimes[0],
      datasets: selectedWellArray.flatMap((well, wellIndex) =>
        well.indicators.map((indicator, indIndex) => ({
          label: `${well.label} - Indicator ${indIndex + 1}`,
          data:
            typeof indicator.materializeRawData === "function"
              ? indicator.materializeRawData()
              : indicator.rawData,
          fill: false,
          borderColor: indicatorColors[indIndex % indicatorColors.length],
          tension: 0.1,
          hidden: !indicator.isDisplayed,
        }))
      ),
    }),
    [indicatorTimes, selectedWellIds]
  );

  // Memoize filteredGraphData to prevent unnecessary re-renders. Materialize
  // {x,y}[] only for the small set of selected wells (filtered storage is
  // typed-array post-apply; full {x,y}[] view across every well would OOM).
  const filteredGraphData = useMemo(
    () => ({
      labels: indicatorTimes[0],
      datasets: selectedWellArray.flatMap((well, wellIndex) =>
        well.indicators.map((indicator, indIndex) => ({
          label: `${well.label} - Indicator ${indIndex + 1}`,
          data:
            typeof indicator.materializeFilteredData === "function"
              ? indicator.materializeFilteredData()
              : indicator.filteredData,
          fill: false,
          borderColor: indicatorColors[indIndex % indicatorColors.length],
          tension: 0.1,
          hidden: !indicator.isDisplayed,
        }))
      ),
    }),
    [indicatorTimes, selectedWellIds]
  );

  // Configuration objects for graph options. yScaleWells controls whether
  // the y-range is computed from the whole plate or just the selected wells.
  const largeGraphConfig = LargeGraphOptions(
    wellArrays,
    extractedIndicatorTimes,
    zoomState,
    zoomMode,
    panState,
    panMode,
    largeGraphYScaleMode === "selected" ? selectedWellArray : wellArrays
  );

  const filteredGraphConfig = FilteredGraphOptions(
    wellArrays,
    filteredGraphData,
    extractedIndicatorTimes,
    annotations,
    filteredGraphYScaleMode === "selected" ? selectedWellArray : wellArrays
  );

  const handleToggleDataShown = () => {
    setIsFiltered((prev) => !prev); // Toggle the filter state
    setShowFiltered((prev) => !prev); // Update context state as well
  };

  // Render the component
  return (
    <div className="combined-component">
      <NavBar
        combinedComponentRef={combinedComponentRef}
        profile={profile}
        setProfile={setProfile}
      />
      <div
        className="combined-component__main-container"
        ref={combinedComponentRef}
      >
        {project ? (
          <div
            className="combined-component__project-frame"
            style={{
              cursor: isApplyingFilters ? "wait" : "default",
            }}
          >
            {isApplyingFilters && (
              <div
                style={{
                  position: "fixed",
                  top: 12,
                  right: 12,
                  background: "rgba(0,0,0,0.78)",
                  color: "#fff",
                  padding: "8px 14px",
                  borderRadius: 6,
                  fontSize: 13,
                  zIndex: 9999,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                  pointerEvents: "none",
                }}
                role="status"
                aria-live="polite"
              >
                Applying filters
                {filterProgress && filterProgress.totalPhases
                  ? ` — phase ${Math.min(
                      filterProgress.phaseIndex + 1,
                      filterProgress.totalPhases
                    )} of ${filterProgress.totalPhases}`
                  : "…"}
                {filterProgress && filterProgress.shardsDone
                  ? ` (${filterProgress.shardsDone}/${filterProgress.totalShards} shards)`
                  : ""}
              </div>
            )}
            <section className="combined-component__wave-container">
              <header className="combined-component__minigraph-header">
                <Typography>All Waves</Typography>
                <Tooltip
                  title="Capture Screenshot of 'All Waves' Grid"
                  disableInteractive
                  arrow
                  placement="top"
                >
                  <IconButton
                    onClick={() => handleScreenshot(miniGraphGridComponentRef)}
                  >
                    <AddAPhotoTwoTone />
                  </IconButton>
                </Tooltip>
              </header>
              <div
                className="combined-component__minigraph ui-surface ui-surface--chart"
                ref={miniGraphGridComponentRef}
              >
                <MiniGraphGrid
                  extractedIndicatorTimes={extractedIndicatorTimes}
                  largeCanvasWidth={largeCanvasWidth}
                  largeCanvasHeight={largeCanvasHeight}
                  smallCanvasWidth={smallCanvasWidth}
                  smallCanvasHeight={smallCanvasHeight}
                  columnLabels={columnLabels}
                  rowLabels={rowLabels}
                  onWellClick={(index) =>
                    handleWellArrayClick(
                      index,
                      wellArrays,
                      selectedWellArray,
                      setSelectedWellArray
                    )
                  }
                  onRowSelectorClick={(rowLabel) =>
                    handleRowSelectorClick(
                      rowLabel,
                      wellArrays,
                      selectedWellArray,
                      setSelectedWellArray
                    )
                  }
                  onColumnSelectorClick={(colIndex) =>
                    handleColumnSelectorClick(
                      colIndex,
                      wellArrays,
                      selectedWellArray,
                      setSelectedWellArray
                    )
                  }
                  onAllSelectorClick={() =>
                    handleAllSelectorClick(
                      wellArrays,
                      selectedWellArray,
                      setSelectedWellArray
                    )
                  }
                />
              </div>
              <div className="combined-component__minigraph-controls">
                <MiniGraphControls
                  handleToggleDataShown={handleToggleDataShown}
                  handleToggleVisibility={handleToggleVisibility}
                />
              </div>
              <header className="combined-component__large-graph-header">
                <Typography>Raw Waves</Typography>
                <Tooltip
                  title="Capture Screenshot of 'Raw Waves' Graph"
                  disableInteractive
                  arrow
                  placement="top"
                >
                  <IconButton
                    onClick={() => handleScreenshot(largeGraphComponentRef)}
                  >
                    <AddAPhotoTwoTone />
                  </IconButton>
                </Tooltip>
              </header>
              {showLargeGraph && (
                <div
                  className="combined-component__large-graph ui-surface ui-surface--chart"
                  ref={largeGraphComponentRef}
                >
                  <LargeGraph
                    ref={largeGraphRef}
                    zoomState={zoomState}
                    zoomMode={zoomMode}
                    panState={panState}
                    panMode={panMode}
                    rawGraphData={rawGraphData}
                    filteredGraphData={filteredGraphData}
                    extractedIndicatorTimes={extractedIndicatorTimes}
                    largeGraphConfig={largeGraphConfig}
                  />
                </div>
              )}
              <div className="combined-component__large-graph-controls">
                <LargeGraphControls
                  resetZoom={resetZoom}
                  zoomState={zoomState}
                  toggleZoomState={toggleZoomState}
                  changeZoomMode={changeZoomMode}
                  panState={panState}
                  togglePanState={togglePanState}
                  changePanMode={changePanMode}
                  showLargeGraph={showLargeGraph}
                  setShowLargeGraph={setShowLargeGraph}
                  yScaleMode={largeGraphYScaleMode}
                  setYScaleMode={setLargeGraphYScaleMode}
                />
              </div>
            </section>
            <section className="combined-component__metrics-filter-container">
              <header className="combined-component__metrics-header">
                <Typography>Metrics</Typography>
                <Tooltip
                  title="Capture Screenshot of 'Metrics' Heatmap"
                  disableInteractive
                  arrow
                >
                  <IconButton
                    onClick={() => handleScreenshot(heatmapComponentRef)}
                  >
                    <AddAPhotoTwoTone />
                  </IconButton>
                </Tooltip>
              </header>
              <div
                className="combined-component__metrics ui-surface ui-surface--chart"
                ref={heatmapComponentRef}
              >
                <Heatmap
                  selectedWellArray={selectedWellArray}
                  timeData={extractedIndicatorTimes}
                  columnLabels={columnLabels}
                  rowLabels={rowLabels}
                  annotationRangeStart={annotationRangeStart}
                  annotationRangeEnd={annotationRangeEnd}
                  metricType={metricType}
                  metricIndicator={metricIndicator}
                />
              </div>
              <div className="combined-component__metrics-controls">
                <MetricsControls
                  setMetricType={setMetricType}
                  metricIndicator={metricIndicator}
                  setMetricIndicator={setMetricIndicator}
                  // annotations={annotations}
                  annotationRangeStart={annotationRangeStart}
                  annotationRangeEnd={annotationRangeEnd}
                  // setAnnotations={setAnnotations}
                  setAnnotationRangeStart={setAnnotationRangeStart}
                  setAnnotationRangeEnd={setAnnotationRangeEnd}
                />
              </div>
              <header className="combined-component__filters-header">
                <Typography>Filtered Waves</Typography>
                <Tooltip
                  title="Capture Screenshot of 'Filtered Waves' Graph"
                  disableInteractive
                  arrow
                >
                  <IconButton
                    onClick={() => handleScreenshot(filteredGraphComponentRef)}
                  >
                    <AddAPhotoTwoTone />
                  </IconButton>
                </Tooltip>
              </header>
              <div
                className={`combined-component__filtered-graph ui-surface ui-surface--chart${
                  !showLargeGraph ? " expanded" : ""
                }`}
                ref={filteredGraphComponentRef}
              >
                <FilteredGraph
                  wellArrays={wellArrays}
                  extractedIndicatorTimes={extractedIndicatorTimes}
                  selectedWellArray={selectedWellArray}
                  filteredGraphData={filteredGraphData}
                  // annotations={annotations}
                  // setAnnotations={setAnnotations}
                  annotationRangeStart={annotationRangeStart}
                  annotationRangeEnd={annotationRangeEnd}
                  setAnnotationRangeStart={setAnnotationRangeStart}
                  setAnnotationRangeEnd={setAnnotationRangeEnd}
                  filteredGraphConfig={filteredGraphConfig}
                />
              </div>
              <div className="combined-component__filter-controls">
                <FilterControls
                  wellArrays={wellArrays}
                  extractedIndicatorTimes={extractedIndicatorTimes}
                  applyEnabledFilters={applyEnabledFilters}
                  showFiltered={showFiltered}
                  setShowFiltered={setShowFiltered}
                  columnLabels={columnLabels}
                  rowLabels={rowLabels}
                  // annotations={annotations}
                  // setAnnotations={setAnnotations}
                  setAnnotationRangeStart={setAnnotationRangeStart}
                  setAnnotationRangeEnd={setAnnotationRangeEnd}
                  handleResetFilteredData={handleResetFilteredData}
                  yScaleMode={filteredGraphYScaleMode}
                  setYScaleMode={setFilteredGraphYScaleMode}
                />
              </div>
            </section>
          </div>
        ) : (
          <NoDataUploaded className="combined-component__no-data" />
        )}
      </div>
    </div>
  );
};
